import { Button } from 'primereact/button';
import { FC, MutableRefObject, Suspense, useEffect, useRef, useState } from 'react';
interface Props {
  filters: DiagnosticModalTableFilters;
  isReset: boolean;
  totalRecords: number;
  hideButton?: number | null;
}
const PAGE_SIZE = 50;
export const OutstandingResetDiagnosticCsvButton: FC<Props> = ({
  filters,
  isReset,
  totalRecords,
  hideButton = null,
}) => {
  const [fetchData, setFetchData] = useState(false);
  const [finishedLoading, setFinishedLoading] = useState(false);
  const [localFilters, setLocalFilters] = useState({ ...filters, page: 0 });
  const [studentsFiltered, setStudentsFiltered] = useState<StudentsData[]>([]);
  const [pageInfo, setPageInfo] = useState<PageInfo | undefined>(undefined);
  const userRedux = useAppSelector(selectUser);
  const isCsvProcessing = useAppSelector(selectedCsvProcess);
  const dispatch = useAppDispatch();

  const totalOfQuerys = Math.ceil(totalRecords / PAGE_SIZE);
  const [queryNumber, setQueryNumber] = useState<number>(1);
  const [progressValueToast, setProgressValueToast] = useState<number>(0);
  const interval = useRef<number | null>(null);
  const downloadToastRef = useRef<Toast | null>(null);
  const successfullToastRef = useRef<Toast | null>(null);

  const exportCSV = async () => {
    const headers = ['Name', 'Classes', 'Site', 'Grade'];
    if (isReset) headers.push('Completed on');
    headers.push('Diagnostic status');
    const csvRows = !isReset
      ? studentsFiltered.map(item => [
          `${item.firstName} ${item.lastName}`,
          item.classes?.map(item => item.name).join(','),
          item.sites?.length ? item.sites[0].name : '',
          item.gradeLevels.length ? item.gradeLevels[0].name : '',
          'outstanding',
        ])
      : studentsFiltered.map(item => [
          `${item.firstName} ${item.lastName}`,
          item.classes?.map(item => item.name).join(','),
          item.sites?.length ? item.sites[0].name : '',
          item.gradeLevels.length ? item.gradeLevels[0].name : '',
          getLastestDiagnostic(item.diagnostics)?.sessionCompletedOn
            ? formatDateWithTimezone(
                getLastestDiagnostic(item.diagnostics)?.sessionCompletedOn,
                userRedux?.activeTimeZone,
              )
            : '',
          'reset',
        ]);

    createAndExportCSV(headers, csvRows);
  };

  const clear = async () => {
    setTimeout(() => {
      setStudentsFiltered([]);
      setFetchData(false);
      setFinishedLoading(false);
      setPageInfo(undefined);
      clearDownloadToast(setQueryNumber, setProgressValueToast, interval, downloadToastRef);
    }, 500);
  };

  useEffect(() => {
    setLocalFilters({ ...filters, page: 0, rows: PAGE_SIZE });
  }, [filters]);

  useEffect(() => {
    if (finishedLoading) {
      exportCSV()
        .then(() => {
          clear().then(() => {
            showSuccessfullToast(successfullToastRef);
            setTimeout(() => {
              dispatch(setCsvProcess(false));
              dispatch(setReportHubQuerysInProgress(false));
            }, 3000);
          });
        })
        .catch(() => {
          dispatch(setCsvProcess(false));
          dispatch(setReportHubQuerysInProgress(false));
        });
    }
  }, [finishedLoading]);

  useEffect(() => {
    if (fetchData && !pageInfo?.hasNextPage) {
      setFinishedLoading(true);
    }
  }, [pageInfo]);

  useEffect(() => {
    if (hideButton) {
      dispatch(setCsvProcess(true));
      setFetchData(true);
      showDownloadToast(downloadToastRef);
    }
  }, [hideButton]);

  return (
    <>
      {!hideButton && (
        <Button
          icon="nc_icon-nc_export icon-sm"
          label="Export Data Table"
          tooltip="Export the filtered data to CSV format"
          aria-label="Export the filtered data to CSV format"
          rounded
          outlined
          disabled={fetchData || isCsvProcessing}
          onClick={() => {
            dispatch(setCsvProcess(true));
            setFetchData(true);
            showDownloadToast(downloadToastRef);
          }}
        ></Button>
      )}
      {fetchData && (
        <Suspense>
          <DiagnosticQueryDataCsv
            students={studentsFiltered}
            fetchData={fetchData}
            filterState={localFilters}
            setStudents={setStudentsFiltered}
            pageInfo={pageInfo}
            setPageInfo={setPageInfo}
            isReset={isReset}
            setProgressValueToast={setProgressValueToast}
            totalOfQuerys={totalOfQuerys}
            interval={interval}
            queryNumber={queryNumber}
            setQueryNumber={setQueryNumber}
          />
        </Suspense>
      )}
      <ToastExportXLSorCSV downloadToastRef={downloadToastRef} clear={clear} progressValueToast={progressValueToast} />
      <ToastSuccessful successfullToastRef={successfullToastRef} />
    </>
  );
};

import { Dispatch, SetStateAction } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { PageInfo } from '../../models/PageInfo';
import { DiagnosticModalTableFilters, StudentsData } from './ResetAndOutstandingDiagnosticModalWrapper';
import { allStudentsWithoutTotal } from '../../graphQLOperations/Students';
import { getLastestDiagnostic } from '../../businessLogicService/getLatestDiagnostic';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { selectUser } from '../../redux/reducers/userSlice';
import { formatDateWithTimezone } from '../../businessLogicService/dateFormatting';
import { StudentsAllStudentsWithoutTotalQuery } from '../../graphQLOperations/__generated__/StudentsAllStudentsWithoutTotalQuery.graphql';
import { selectedCsvProcess, setCsvProcess } from '../../redux/reducers/csvProcessSlice';
import { Toast } from 'primereact/toast';
import {
  clearDownloadToast,
  showDownloadToast,
  showSuccessfullToast,
} from '../../businessLogicService/exportToastHandler';
import ToastExportXLSorCSV from '../ExportCSV/ToastExportXLSorCSV';
import ToastSuccessful from '../ExportCSV/ToastSuccessful';
import { createAndExportCSV } from '../../businessLogicService/createAndExportGenericCSV';
import { setReportHubQuerysInProgress } from '../../redux/reducers/reportHubQuerysInProgress';

export interface QueryDataProp {
  students: StudentsData[];
  fetchData: boolean;
  filterState: DiagnosticModalTableFilters;
  setStudents: Dispatch<SetStateAction<StudentsData[]>>;
  setPageInfo: Dispatch<SetStateAction<PageInfo | undefined>>;
  pageInfo: PageInfo | undefined;
  isReset: boolean;
  setProgressValueToast: (p: number) => void;
  totalOfQuerys: number;
  interval: MutableRefObject<number | null | undefined>;
  queryNumber: number;
  setQueryNumber: Dispatch<SetStateAction<number>>;
}

export const DiagnosticQueryDataCsv = ({
  setStudents,
  filterState,
  fetchData,
  setPageInfo,
  students,
  isReset,
  pageInfo,
  setProgressValueToast,
  totalOfQuerys,
  interval,
  queryNumber,
  setQueryNumber,
}: QueryDataProp) => {
  const startDateAndTime =
    filterState.filters.completedOn && filterState.filters.completedOn[0]
      ? new Date(filterState.filters.completedOn[0])
      : null;
  let endDateAndTime =
    filterState.filters.completedOn && filterState.filters.completedOn[1]
      ? new Date(filterState.filters.completedOn[1])
      : null;

  if (startDateAndTime && !endDateAndTime) {
    endDateAndTime = new Date(startDateAndTime);
    endDateAndTime.setHours(23);
    endDateAndTime.setMinutes(59);
  } else if (startDateAndTime && endDateAndTime) {
    endDateAndTime.setHours(23);
    endDateAndTime.setMinutes(59);
  }
  const allStudentsdata = useLazyLoadQuery<StudentsAllStudentsWithoutTotalQuery>(
    allStudentsWithoutTotal,
    {
      filter: {
        classIds: filterState.filters.classes?.length ? filterState.filters.classes : null,
        gradeLevelIds: filterState.filters.grade?.length ? filterState.filters.grade.map(gr => gr.id) : null,
        siteIds: filterState.filters.site?.length ? filterState.filters.site.map(st => st.id) : null,
        studentNameContains: filterState.filters.name,
        onlyWithOutstandingDiagnostic: isReset ? null : true,
        onlyWithResetDiagnostic: isReset ? true : null,
        lastDiagnosticCompletedFrom: startDateAndTime ? startDateAndTime : null,
        lastDiagnosticCompletedTo: endDateAndTime ? endDateAndTime : null,
      },
      order: [{ firstName: filterState.sortOrder === 1 ? 'ASC' : 'DESC' }],
      first: filterState.rows,
      last: null,
      after: pageInfo?.endCursor,
    },

    { fetchPolicy: 'network-only', fetchKey: JSON.stringify(filterState) },
  );

  useEffect(() => {
    if (queryNumber <= totalOfQuerys) {
      const calc = Math.round((queryNumber / totalOfQuerys) * 100);
      interval.current = calc;
      setProgressValueToast(calc);
      setQueryNumber((e: number) => e + 1);
    }
    if (allStudentsdata.allStudents?.nodes && fetchData) {
      setPageInfo(allStudentsdata.allStudents.pageInfo);
      {
        setStudents([...students, ...allStudentsdata.allStudents.nodes] as StudentsData[]);
      }
    }
  }, [allStudentsdata]);

  return <div style={{ display: 'none' }}></div>;
};
